// Three.js scene host for the awning visualizer.
// Mirrors the renderer/lighting quality of the pergola configurator
// (PCFSoft 4096 shadows, ACES tone mapping, sRGB output, HDRI env).

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import { AwningMaterials } from './AwningMaterials.js';
import { AwningBuilder } from './AwningBuilder.js';
import { AwningSun } from './AwningSun.js';
import { ShadeScorer } from './ShadeScorer.js';

export class AwningApp {
  constructor(canvas) {
    this.canvas = canvas;
    this.mounted = false;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xd8e5ec);
    this.scene.fog = new THREE.Fog(0xd8e5ec, 18, 60);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 200);
    // Camera is FIXED in world space — it does not follow facingDeg. As the
    // user rotates the worldGroup via the compass, the house+awning assembly
    // visibly turns in front of a stationary camera (turntable view).
    // Camera distance scales with awning width; recomputed in _orientCamera
    // when width changes.
    this._target = new THREE.Vector3(0, 1.7, 2.2);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = Math.PI * 0.49;
    this.controls.minDistance = 3;
    this.controls.maxDistance = 22;
    this._orientCamera();

    // ── Lighting ─────────────────────────────────────────────────────
    this.ambient = new THREE.AmbientLight(0xffffff, 0.25);
    this.scene.add(this.ambient);

    this.hemi = new THREE.HemisphereLight(0xc0e0ff, 0xf0e8d8, 0.35);
    this.scene.add(this.hemi);

    this.sunLight = new THREE.DirectionalLight(0xfff4e0, 1.0);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 4096;
    this.sunLight.shadow.mapSize.height = 4096;
    this.sunLight.shadow.camera.near = 0.5;
    this.sunLight.shadow.camera.far = 60;
    this.sunLight.shadow.camera.left = -10;
    this.sunLight.shadow.camera.right = 10;
    this.sunLight.shadow.camera.top = 10;
    this.sunLight.shadow.camera.bottom = -10;
    this.sunLight.shadow.bias = -0.0005;
    this.sunLight.shadow.normalBias = 0.02;
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);

    // ── Materials, builder, sun, scorer ─────────────────────────────
    this.materials = new AwningMaterials();
    this.materials.attachEnvMap(this.materials.envMap);
    this.scene.environment = this.materials.envMap;
    this.scene.environmentIntensity = 1.0;

    this.builder = new AwningBuilder(this.scene, this.materials);
    this.sun = new AwningSun();
    this.scorer = new ShadeScorer();

    this.textureLoader = new THREE.TextureLoader();

    // Try to load the high-quality HDRI from the pergola public folder.
    this._loadHDRI();

    this._lastShadeScore = null;
    this._scoreCallback = null;

    this._tick = this._tick.bind(this);
    this._resize = this._resize.bind(this);
    window.addEventListener('resize', this._resize);
  }

  mount() {
    if (this.mounted) return;
    this.mounted = true;
    this._resize();
    this.renderer.setAnimationLoop(this._tick);
  }

  unmount() {
    this.renderer.setAnimationLoop(null);
    this.mounted = false;
  }

  _resize() {
    const wrap = this.canvas.parentElement;
    if (!wrap) return;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight || 420;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  _loadHDRI() {
    const loader = new RGBELoader();
    const basePath = import.meta.env.BASE_URL || '/';
    loader.load(
      `${basePath}hdri/kloppenheim_06_puresky_1k.hdr`,
      (tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.environment = tex;
        this.scene.background = tex;        // photoreal sky background
        this.scene.fog = null;               // sky carries depth on its own
        this.scene.environmentIntensity = 1.2;
        this.materials.attachEnvMap(tex);
        // HDRI carries plenty of fill — pull back manual lights so they don't double-light.
        this.ambient.intensity = 0.06;
        this.hemi.intensity = 0.08;
        this.sunLight.intensity = 0.95;
        this.renderer.toneMappingExposure = 0.85;
      },
      undefined,
      (err) => console.warn('[awning] HDRI load failed; using procedural env.', err),
    );
  }

  // ── Sun/light update on every tick ─────────────────────────────────
  _updateSun() {
    const p = this.sun.getLightParams();
    this.sunLight.position.set(p.x, p.y, p.z);
    this.sunLight.color.setHex(p.color);
    this.sunLight.intensity = p.intensity;
    if (p.isNight) {
      // Pull ambient up a touch so we don't go pitch black.
      this.ambient.intensity = 0.45;
    }
  }

  _tick() {
    this._updateSun();
    this.controls.update();
    this.renderer.render(this.scene, this.camera);

    // Score shade coverage at ~6 Hz.
    if (!this._scoreEvery) this._scoreEvery = 0;
    this._scoreEvery++;
    if (this._scoreEvery % 10 === 0 && this.builder.getFabricMesh()) {
      const score = this.scorer.score(
        this.sunLight.position,
        this.builder.getShadeCasters(),
        this.builder.getPatioMesh(),
      );
      if (this._scoreCallback && score !== this._lastShadeScore) {
        this._scoreCallback(score);
        this._lastShadeScore = score;
      }
    }
  }

  // ── Public API used by wizard ──────────────────────────────────────
  setTier(tier) {
    this.builder.setTier(tier);
  }
  setWidthKey(key) {
    this.builder.setWidthKey(key);
    this._orientCamera();      // camera distance scales with width
  }
  setExtension(t) {
    this.builder.setExtension(t);
  }
  setDropScreenExtension(t) {
    this.builder.setDropScreenExtension(t);
  }
  setPitchDeg(deg) {
    this.builder.setPitchDeg(deg);
  }
  // Rotate only the worldGroup. Sun stays in real astronomical position;
  // camera stays fixed in world space — turntable view.
  setFacingDeg(deg) {
    this.builder.setFacingDeg(deg);
  }

  // Camera position scales with awning width but is FIXED in world space —
  // it does not follow facingDeg. The user sees the house+awning rotate as
  // a turntable in front of a stationary camera. Default angle is a slight-
  // elevation 3/4 front view that puts the awning + patio + wall all in frame.
  _orientCamera() {
    const widthM  = this.builder ? this.builder.getWidthM() : 4.572;
    const camDist = 4.5 + widthM * 0.85;

    // Front 3/4 view: camera sits well IN FRONT of the patio (large +Z),
    // slightly off-center (+X), moderate elevation. From here the customer
    // sees the awning top + front edge, the patio with furniture, and the
    // wall+door in the background on the right — like standing in the
    // backyard looking back at the house.
    this.camera.position.set(-camDist * 0.3, 3.0, camDist * 0.75);
    this.controls.target.copy(this._target);
    this.controls.update();
  }
  setStateChosen(abbr) {
    this.sun.setStateCoord(abbr);
  }
  setTimeMinutes(t) {
    this.sun.setTimeMinutes(t);
  }
  setDateFromString(s) {
    this.sun.setDateFromString(s);
  }
  setManualAzimuth(deg) {
    this.sun.setManualAzimuth(deg);
  }
  clearManualAzimuth() {
    this.sun.clearManualAzimuth();
  }
  setFabric(record) {
    this.materials.setFabric(record, this.textureLoader);
  }
  onShadeScore(cb) {
    this._scoreCallback = cb;
  }
  getSunController() {
    return this.sun;
  }
}
